import React, { useState, useEffect, useRef } from "react";
import { useAppSelector } from "../../app/hooks";
import moment from "moment";
import { commonGetService } from "../../utils/properties";
import { OverlayTrigger, Popover, Spinner } from "react-bootstrap";
import "../../styles/Dashboard/timeLineDetails.scss";
import { showToast } from "../../features/Toaster/toastslice";

interface Activity {
  start_time: number;
  end_time: number;
  app: string | null;
  category: string;
  start_time_am_pm: string;
  end_time_am_pm: string;
  duration: string;
  durationInSec: number;
}

interface TimeLine extends Activity {
  start: number;
  end: number;
}

interface DailyData {
  date: string;
  total_worked_time: string;
  total_hours: string;
  idle_time: string;
  start_time_in_am_pm: string;
  end_time_in_am_pm: string;
  start_time: number[];
  end_time: number[];
  activities: Activity[] | null;
  timeLine?: TimeLine[] | null;
}

const TimeLine = () => {
  const [chartLoading, setIsChartLoading] = useState(true);
  const [timeLineData, setTimeLineData] = useState<DailyData[]>([]);

  const filterData = useAppSelector((state) => state.filterData);
  const { startDate, endDate, empDeviceId } = filterData; // Destructuring the filterData state value

  const controllerRef = useRef<AbortController | null>(null);
  const timelineScrollRef = useRef<HTMLDivElement | null>(null);

      const [isTablet, setIsTablet] = useState(false);

useEffect(() => {
  const checkTablet = () => {
    const w = window.innerWidth;
    setIsTablet(w >= 768 && w <= 991);
  };
  checkTablet();
  window.addEventListener("resize", checkTablet);
  return () => window.removeEventListener("resize", checkTablet);
}, []);

  useEffect(() => {
    if (empDeviceId === "") return;
    getTimelineData();
    setIsChartLoading(true);

    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      setTimeLineData([]);
    };
  }, [filterData]);

  useEffect(() => {
    if (!chartLoading && timelineScrollRef.current) {
      const container = timelineScrollRef.current;

      // Scroll to 9 AM
      const scrollToHour = 9;
      const scrollPosition = (container.scrollWidth * scrollToHour) / 24;

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [chartLoading]);

  const getTimelineData = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    // Create a new AbortController for the new request
    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;
    let fromDate = moment(startDate).format("YYYY-MM-DD");
    let toDate = moment(endDate).format("YYYY-MM-DD");

    commonGetService(
      `/api/1.0/dashboard/user/data?empCode=${empDeviceId}&startDate=${fromDate}&endDate=${toDate}`,
      signal
    ).then((res) => {
      setIsChartLoading(false);
      if (res.status === 200) {
        let reversedData = res.data?.reverse();
        reversedData?.forEach((item: DailyData) => {
          item.timeLine = [];
          const timeline: TimeLine[] = [];

          item?.activities?.forEach((act) => {
            const start = moment(act.start_time_am_pm, "hh:mm:ss A");
            const end = moment(act.end_time_am_pm, "hh:mm:ss A");

            let startMoment = start.clone();
            let endMoment = end.clone();

            // Handle cross-midnight activity
            if (endMoment.isBefore(startMoment)) {
              endMoment.add(1, "day");
            }

            const dayStart = startMoment.clone().startOf("day");

            const startSeconds = startMoment.diff(dayStart, "seconds");
            const endSeconds = endMoment.diff(dayStart, "seconds");

            timeline.push({
              start: startSeconds / 86400, // 24 * 60 * 60
              end: endSeconds / 86400,
              start_time: act.start_time,
              end_time: act.end_time,
              app: act.app,
              category: act.category,
              start_time_am_pm: act.start_time_am_pm,
              end_time_am_pm: act.end_time_am_pm,
              duration: act.duration,
              durationInSec: act.durationInSec,
            });
          });

          item.timeLine = timeline;
        });

        setTimeLineData(reversedData);
      } else {
        setIsChartLoading(true);
        // dispatch(
        //   showToast({
        //     message: "Something went wrong in API.",
        //     status: "retry",
        //     visible: true,
        //   })
        // );
      }
    });
  };
  // const formatDate = (str: string) => {
  //   if(!str) return

  //   const [datePart, year] = str.split(" - ");
  //   const [dayMonth] = datePart.split(", ");

  //   const dateObj = new Date(`${dayMonth} ${year}`);

  //   const parts = dateObj
  //     .toLocaleDateString("en-GB", {
  //       day: "numeric",
  //       month: "long",
  //       weekday: "long",
  //       year: "numeric",
  //     })
  //     .split(", ");

  //   const weekday = parts[0]; // Monday
  //   const rest = parts[1]; // 5 January 2026

  //   const [day, month, yearVal] = rest.split(" ");


  //   return `${day} ${month}, ${weekday} ${yearVal}`;
  // };

  const formatDate = (str: string) => {
    if (!str) return "";

    // Guard: must contain " - " to be parseable
    if (!str.includes(" - ")) return str; // 👈 return raw string if format unexpected

    const [datePart, year] = str.split(" - ");
    if (!datePart || !year) return str; // 👈 guard against missing parts

    const [dayMonth] = datePart.split(", ");
    if (!dayMonth) return str;

    const dateObj = new Date(`${dayMonth} ${year}`);
    if (isNaN(dateObj.getTime())) return str; // 👈 guard against invalid date

    const parts = dateObj
        .toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            weekday: "long",
            year: "numeric",
        })
        .split(", ");

    if (parts.length < 2) return str;

    const weekday = parts[0];
    const rest = parts[1];

    const [day, month, yearVal] = rest.split(" ");
    if (!day || !month || !yearVal) return str;

    return `${day} ${month}, ${weekday} ${yearVal}`;
};

  const timeList = [
    "12 AM",
    "1 AM",
    "2 AM",
    "3 AM",
    "4 AM",
    "5 AM",
    "6 AM",
    "7 AM",
    "8 AM",
    "9 AM",
    "10 AM",
    "11 AM",
    "12 PM",
    "1 PM",
    "2 PM",
    "3 PM",
    "4 PM",
    "5 PM",
    "6 PM",
    "7 PM",
    "8 PM",
    "9 PM",
    "10 PM",
    "11 PM",
  ];

  return (
    <div className="line-chart-ctr">
      <div className="d-flex flex-column timeline-container">
        <div className="py-3 px-0">
          {/* <h4>Time lines</h4> */}
          <h4>Work pattern</h4>
        </div>
        {chartLoading ? (
          <div className="d-flex justify-content-center align-items-center crt-loader spin-h">
            <img src="/images/loader/content_loader.gif" />
          </div>
        ) : (
          <div className="tl-chart w-100 common-scroll" ref={timelineScrollRef}>
            <table>
              <thead>
                <tr>
                  <th>
                    <div>
                      <h6 className="d-hid">Title</h6>
                    </div>
                  </th>
                  {timeList?.map((heading, i) => (
                    <th key={`${heading + i}`}>
                      <div>
                        <h6>{heading}</h6>
                      </div>
                    </th>
                  ))}

{isTablet && (
  <th colSpan={2} className="tablet-st-col tablet-st-col--header"  style={{ minWidth: "10rem", width: "10rem" }} >
    <div>
      <h6 className="d-hid">Time</h6>
    </div>
  </th>
)}
                </tr>
              </thead>
              <tbody>
                {timeLineData?.map((actData: DailyData, ind: number) => (
                  <tr key={ind}>
                    <td>
                      <div className="date-col">
                        <p>{actData?.date}</p>
                        <p>
                          <span>Total Hours : </span>
                          {actData?.total_hours}
                        </p>
                      </div>
                    </td>
                    <td colSpan={24}>
                      <div className="ps-bar-col">
                        <div className="time-dtl-cont">
                          <div className="time-stick-wkd">
                            <div className="time-dtl wkd-time">
                              <p>
                                Worked Time :{" "}
                                <span className="gr-code">
                                  {actData?.total_worked_time}
                                </span>
                              </p>
                              <p>
                                Idle Time :{" "}
                                <span className="red-code">
                                  {actData?.idle_time}
                                </span>
                              </p>
                            </div>
                          </div>
                          <div className="time-stick-st time-stick-st--desktop">
                            <div className="time-dtl st-time">
                              <p>
                                <span className="gre-code">Start Time : </span>
                                {actData?.start_time_in_am_pm}
                              </p>
                              <p>
                                <span className="gre-code">End Time : </span>
                                {actData?.end_time_in_am_pm}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="ps-bar-pt">
                          {actData?.timeLine?.map((segment, j) => (
                            <OverlayTrigger
                              key={j}
                              trigger={["hover", "focus"]}
                              placement="bottom"
                              overlay={
                                <Popover className="tooltip-timeline-popover">
                                  <Popover.Header className="tooltip-header">
                                    <p className="toolltip-date">
                                      {/* {formatDate(actData?.date )} */}
                                     {actData?.date?formatDate(actData?.date):""} 
                                    </p>
                                    <p className="tooltip-item">
                                      <span className="tooltip-label">
                                        Duration
                                      </span>
                                      -
                                      <span className="tooltip-value">
                                        {segment.duration}
                                      </span>
                                    </p>
                                  </Popover.Header>
                                  <Popover.Body className="tooltip-body">
                                    <>
                                      <p className="tooltip-item">
                                        <span className="tooltip-label">
                                          Application
                                        </span>
                                        -
                                        <span className="tooltip-value">
                                          {segment.app}
                                        </span>
                                      </p>
                                      <p className="tooltip-item">
                                        <span className="tooltip-label">
                                          Category
                                        </span>
                                        -
                                        <span
                                          className={`tooltip-value ${segment.category == "productive" ||
                                            segment.category == "active"
                                            ? "green"
                                            : segment.category ==
                                              "unproductive" ||
                                              segment.category ==
                                              "inactive" ||
                                              segment.category == "idle"
                                              ? "red"
                                              : ""
                                            }`}
                                        >
                                          {segment.category}
                                        </span>
                                      </p>{" "}
                                      <p className="tooltip-item">
                                        <span className="tooltip-label">
                                          Start
                                        </span>
                                        -
                                        <span className="tooltip-value">
                                          {segment.start_time_am_pm}
                                        </span>
                                      </p>
                                      <p className="tooltip-item">
                                        <span className="tooltip-label">
                                          End
                                        </span>
                                        -
                                        <span className="tooltip-value">
                                          {segment.end_time_am_pm}
                                        </span>
                                      </p>
                                    </>
                                  </Popover.Body>
                                </Popover>
                              }
                            >
                              <div
                                className="ps-bar-active"
                                style={{
                                  left: `${segment.start * 100}%`,
                                  width: `${(segment.end - segment.start) * 100
                                    }%`,
                                }}
                              />
                            </OverlayTrigger>
                          ))}
                        </div>
                      </div>
                    </td>

    {isTablet && (
  <td colSpan={2} className="tablet-st-col" style={{ minWidth: "10rem", width: "10rem" }} >
    <div className="time-dtl st-time">
      <p>
        <span className="gre-code">Start Time : </span>
        {actData?.start_time_in_am_pm}
      </p>
      <p>
        <span className="gre-code">End Time : </span>
        {actData?.end_time_in_am_pm}
      </p>
    </div>
  </td>
)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeLine;